import { useCallback } from 'react';
import { useMutation, fetchQuery } from 'react-relay';
import { UnifiedGamificationMutation } from 'graphql/__generated__/UnifiedGamificationMutation.graphql';
import { UNIFIED_GAMIFICATION_MUTATION } from 'graphql/UnifiedGamificationMutation';
import { RECOGNITION_SCHEDULE_QUERY } from 'graphql/RecognitionScheduleQuery';
import { KUDOS_SUMMARY_QUERY } from 'graphql/KudosSummaryQuery';
import { environment } from '../services/relay';
import { sendRecognitionButtonClickEvent } from 'analytics/kudosAnalytics';

interface GiveRecognitionParams {
  currentProfileAaid: string;
  comment: string;
  loggedInUserAaid: string;
  pathProfileId: string;
}

interface Props {
  onSuccess: () => void;
  onComplete: () => void;
  profileInternalId: string;
}

export const useGiveRecognition = ({
  onSuccess,
  onComplete,
  profileInternalId,
}: Props) => {
  const [commitRecognition, isInFlight] =
    useMutation<UnifiedGamificationMutation>(UNIFIED_GAMIFICATION_MUTATION);

  const refetchRecognitionSchedule = useCallback((loggedInUserAaid: string) => {
    fetchQuery(environment, RECOGNITION_SCHEDULE_QUERY, {
      aaid: loggedInUserAaid,
    }).subscribe({
      next: () => {
        // Data will be automatically updated in the component
      },
      error: (error: unknown) => {
        console.error('Failed to refetch recognition schedule', error);
      },
    });
  }, []);

  const refetchKudosSummary = useCallback(() => {
    fetchQuery(environment, KUDOS_SUMMARY_QUERY, {
      internalId: profileInternalId,
    }).subscribe({
      next: () => {
        // Data will be automatically updated in the component
      },
      error: (error: unknown) => {
        console.error('Failed to refetch kudos summary', error);
      },
    });
  }, [profileInternalId]);

  const giveRecognition = useCallback(
    (params: GiveRecognitionParams) => {
      commitRecognition({
        variables: {
          aaid: params.currentProfileAaid,
          comment: params.comment,
          value: 100,
        },
        onCompleted: (response, errors) => {
          const authenticateRecognitionVal =
            response?.unified?.gamification?.sendRecognition;

          if (!authenticateRecognitionVal?.success || errors) {
            if (errors) {
              console.error('Giving recognition failed', errors);
            }
          } else {
            refetchRecognitionSchedule(params.loggedInUserAaid);
            refetchKudosSummary();
            sendRecognitionButtonClickEvent({
              recognitionReason: params.comment,
              senderAaid: params.loggedInUserAaid,
              receiverAaid: params.currentProfileAaid,
              profileInternalId: params.pathProfileId,
            });
            onSuccess();
          }

          onComplete();
        },
        onError: (error) => {
          console.error('Giving recognition failed', error);
          onComplete?.();
        },
      });
    },
    [
      commitRecognition,
      refetchRecognitionSchedule,
      refetchKudosSummary,
      onSuccess,
      onComplete,
    ],
  );

  return { giveRecognition, isSubmitting: isInFlight };
};
